// ===========================================
// CARD PAYMENT MODAL COMPONENT
// ===========================================

import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { StripeProvider } from '../providers/StripeProvider';
import { PaymentForm } from './PaymentForm';
import { paymentsApi } from '../services/api';

interface CardPaymentModalProps {
  checkId: string;
  amount: number; // in cents
  tipAmount: number; // in cents
  onSuccess: (paymentId: string) => void;
  onCancel: () => void;
}

export function CardPaymentModal({
  checkId,
  amount,
  tipAmount,
  onSuccess,
  onCancel,
}: CardPaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Card charged but our record failed — retrying the payment would
  // double-charge, so the only exit is Close
  const [chargedUnrecorded, setChargedUnrecorded] = useState(false);

  // Create PaymentIntent on mount
  const createIntentMutation = useMutation({
    mutationFn: async () => {
      const response = await paymentsApi.createIntent(checkId, amount, tipAmount);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.data?.clientSecret) {
        setClientSecret(data.data.clientSecret);
      } else if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        setError('Failed to initialize payment. Please try again.');
      }
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create payment. Please try again.');
    },
  });

  // Confirm payment on backend after Stripe success. The card IS charged at
  // this point — recording it can retry (and the payment_intent.succeeded
  // webhook is the server-side backstop), but we never claim success while
  // the record is missing.
  const confirmPaymentMutation = useMutation({
    retry: 3,
    mutationFn: async (paymentIntentId: string) => {
      const response = await paymentsApi.confirmPayment(paymentIntentId);
      if (!(response as any)?.success && !(response as any)?.data?.success) {
        throw new Error((response as any)?.error?.message || 'Recording failed');
      }
      return response.data;
    },
    onSuccess: (data) => {
      onSuccess(data?.data?.id || data?.id || 'payment-complete');
    },
    onError: (err: any) => {
      console.error('Backend confirmation failed after retries:', err);
      setChargedUnrecorded(true);
      setError(
        'The card was charged, but recording it hit a snag — it will appear in Recent Payments within a minute. Do not charge the guest again.'
      );
    },
  });

  // Create payment intent on mount
  useEffect(() => {
    createIntentMutation.mutate();
  }, []);

  const handlePaymentSuccess = (paymentIntentId: string) => {
    confirmPaymentMutation.mutate(paymentIntentId);
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-600" />
            <h2 className="font-semibold text-lg">Card Payment</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            disabled={confirmPaymentMutation.isPending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Loading state - creating payment intent */}
          {createIntentMutation.isPending && (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-slate-600">Initializing payment...</p>
            </div>
          )}

          {/* Error state */}
          {error && (!clientSecret || chargedUnrecorded) && (
            <div className="py-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {chargedUnrecorded ? 'Card charged — recording pending' : 'Payment Error'}
                </h3>
                <p className="text-slate-600 text-sm mb-4">{error}</p>
                <div className="flex gap-2">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                  >
                    {chargedUnrecorded ? 'Close' : 'Cancel'}
                  </button>
                  {/* Never re-create an intent after a successful charge —
                      that path double-charges the guest */}
                  {!chargedUnrecorded && (
                    <button
                      onClick={() => {
                        setError(null);
                        createIntentMutation.mutate();
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment form */}
          {clientSecret && !chargedUnrecorded && (
            <StripeProvider clientSecret={clientSecret}>
              <PaymentForm
                amount={amount}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onCancel={onCancel}
              />
            </StripeProvider>
          )}

          {/* Confirming payment on backend */}
          {confirmPaymentMutation.isPending && (
            <div className="absolute inset-0 bg-white/90 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
                <p className="text-slate-600">Confirming payment...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer with amount summary */}
        {clientSecret && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span>{formatCurrency(amount - tipAmount)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-600">Tip</span>
                <span>{formatCurrency(tipAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span>{formatCurrency(amount)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
